import type { AnalysisSelectedSeries } from '@src/components/backend/payload-typing';

import type {
  AnalysisResult,
  // BiomarkerRepresentation,
  // OCGCurve,
  BiomarkerGroupType,
  BiomarkerOCGCurveType,
  BiomarkerPixelMask,
  BiomarkerType,
  // SalientIndexType,
  InterpolatedImageIndexItem,
  LabelProbabilityData,
  MaskTypeBehaviour,
  OCGCurveType,
  OCGTerritory,
  SalientIndexItem,
  SalientIndexType,
} from './data-typing';

import { BiomarkerAHAData } from './biomarker-aha-values';
import { BiomarkerMask } from './biomarker-mask';
import { BiomarkerOCGCurve } from './biomarker-ocg-curves';
import { BiomarkerQuantitativeData } from './biomarker-quant-data';

// import { StaticImage } from './biomarker-static-images';
import { SalientIndex } from './salient-index';

export class BiomarkerManager {
  private BiomarkerMasksArray: BiomarkerMask[];
  private BiomarkerCurvesArray: BiomarkerOCGCurve[];
  private BiomarkerQuantitativeDataArray: BiomarkerQuantitativeData[];
  private BiomarkerAHADataArray: BiomarkerAHAData[];
  private LabelProbabilitiesArray: LabelProbabilityData[];
  // private StaticImageArray: StaticImage[];
  private SalientPhases: SalientIndex[];
  private analyzedSeries: AnalysisSelectedSeries[] = [];
  private interpolatedImageIndexes: InterpolatedImageIndexItem[] = [];

  private availableBiomarkerMasks: Partial<
    Record<MaskTypeBehaviour, BiomarkerType[]>
  > = {};

  private availableBiomarkerCurvesByTerritory: Partial<
    Record<OCGCurveType, OCGTerritory[]>
  > = {};

  private curveTypeToLabelMap: Record<OCGCurveType, string> = {
    MYOSI: 'Signal Intensity Myocardial (norm to ED)',
    LVBPSI: 'Signal Intensity LV Blood Pool (norm to ED)',
    LVBPAREA: 'Area LV Blood Pool',
    StressOCG: 'Stress OCG (norm to ED)',
    RecoveryOCG: 'Recovery OCG (norm to ED)',
    StressLVArea: 'Stress LV Cavity Area',
    RecoveryLVArea: 'Recovery LV Cavity Area',
    StressCircumferentialStrain: 'Stress Circumferential Strain',
    RecoveryCircumferentialStrain: 'Recovery Circumferential Strain',
    StressRadialStrain: 'Stress Radial Strain',
    RecoveryRadialStrain: 'Recovery Radial Strain',
    StressAreaStrain: 'Stress Area Strain',
    RecoveryAreaStrain: 'Recovery Area Strain',
  };

  private biomarkerInfo: Partial<
    Record<
      BiomarkerType,
      {
        label: string;
        min?: number;
        mid?: number;
        max?: number;
        normal?: { value: number; label: string };
      }
    >
  > = {
    ocgDiaMaxIntensity: {
      label: 'B-MORE diastole',
      min: 1.02,
      mid: 1.1,
      max: 1.3,
      normal: {
        value: 1.02,
        label: '> 1.02',
      },
    },
    ocgEntropy: {
      label: 'B-MORE micro',
      min: 4.1,
      mid: 4.17,
      max: 4.5,
      normal: {
        value: 4.1,
        label: '> 4.1',
      },
    },
    Vasoconstriction: {
      label: 'Vasoconstriction',
      min: -75,
      mid: 0,
      max: -40,
      normal: {
        value: -40,
        label: '> -40',
      },
    },
    BMORE: {
      label: 'B-MORE',
      min: -10,
      mid: 5,
      max: 20,
      normal: {
        value: 5,
        label: '> 5',
      },
    },
    BMOREMax: {
      label: 'B-MORE max',
      min: -10,
      mid: 5,
      max: 20,
      normal: {
        value: 7,
        label: '> 7',
      },
    },
    TemporalOCGMedian: {
      label: 'Temporal OCG Median',
      min: 0.15,
      mid: 0.3,
      max: 0.5,
      normal: {
        value: 0.3,
        label: '0.15 - 0.50',
      },
    },
    entroED_intensity: {
      label: 'Entropy ED Intensity',
    },
    entroSignalAmplitude: {
      label: 'Entropy Signal Amplitude',
    },
    energyOscillationIndex: {
      label: 'Energy Oscillation Index',
    },
    ED_p90: {
      label: 'ED 90th Percentile',
    },
    ED_entropy: {
      label: 'ED Entropy',
    },
    regional_oxygenation_wall_vulnerability: {
      label: 'Regional ES oxygenation-wall vulnerability',
      min: 0,
      mid: 2,
      max: 4,
    },
    regional_ed_oxygenation_wall_vulnerability: {
      label: 'Regional ED oxygenation-wall vulnerability',
      min: 0,
      mid: 2,
      max: 4,
    },
    circumferential_strain_spectral_bandwidth: {
      label: 'Circumferential strain spectral bandwidth',
      min: 0,
      mid: 2,
      max: 4,
    },
    image_es_nonresponse_mask: {
      label: 'Image ES oxygenation nonresponse',
      min: 0,
      mid: 0.5,
      max: 1,
    },
    image_ed_nonresponse_mask: {
      label: 'Image ED oxygenation nonresponse',
      min: 0,
      mid: 0.5,
      max: 1,
    },
    stress_to_recovery_ocg_absolute_remodeling_area: {
      label: 'Stress-to-recovery absolute OCG remodeling area',
      min: 0,
      mid: 2,
      max: 4,
    },
  };

  constructor() {
    this.BiomarkerMasksArray = [];
    this.BiomarkerCurvesArray = [];
    this.BiomarkerQuantitativeDataArray = [];
    // this.StaticImageArray = [];
    this.SalientPhases = [];
    this.BiomarkerAHADataArray = [];
    this.LabelProbabilitiesArray = [];
    // this.InterpolatedImageIndexArray = [];
  }

  // TODO: Functions return arrays, but for some of them we just need the first result.
  public populateFromResponse(analysisResult: AnalysisResult) {
    this.resetState();

    const {
      BiomarkerMasks,
      BiomarkerCurves,
      SalientIndexes,
      BiomarkerQuantitativeData: quantData,
      // StaticImages,
      Metadata,
      BiomarkerAHAData: BiomarkerAHADataArray,
      LabelProbabilities = [],
    } = analysisResult;

    const { selectedSeries = [], interpolatedImageIndexes = [] } = Metadata;
    this.analyzedSeries = selectedSeries;
    this.interpolatedImageIndexes = interpolatedImageIndexes;

    for (let i = 0; i < BiomarkerMasks.length; i++) {
      const {
        biomarkerName,
        imageUID,
        data,
        isDynamic,
        sliceIndex,
        refImageType,
        range,
      } = BiomarkerMasks[i];

      const biomarkerMask = new BiomarkerMask(
        isDynamic,
        biomarkerName,
        imageUID,
        data,
        sliceIndex,
        refImageType,
        range,
      );

      this.BiomarkerMasksArray.push(biomarkerMask);
    }

    for (let i = 0; i < BiomarkerCurves.length; i++) {
      const {
        biomarkerName,
        data,
        sliceIndex,
        territory,
        normalization,
        interpolatedIndexes,
        isCycleNormalized,
        cycleType,
        startFrame,
        endFrame,
        esFrame,
        esPhaseIndex,
        edPhaseIndexes,
      } = BiomarkerCurves[i];
      const biomarkerCurve = new BiomarkerOCGCurve(
        biomarkerName,
        territory,
        normalization,
        data,
        sliceIndex,
        interpolatedIndexes,
        isCycleNormalized,
        cycleType,
        startFrame,
        endFrame,
        esFrame,
        esPhaseIndex,
        edPhaseIndexes,
      );
      this.BiomarkerCurvesArray.push(biomarkerCurve);
    }

    for (let i = 0; i < quantData.length; i++) {
      const { biomarkerName, data, sliceIndex } = quantData[i];
      const biomarkerQuantitativeData = new BiomarkerQuantitativeData(
        biomarkerName,
        data,
        sliceIndex,
      );
      this.BiomarkerQuantitativeDataArray.push(biomarkerQuantitativeData);
    }

    // for (let i = 0; i < StaticImages.length; i++) {
    //   const staticImage = new StaticImage(
    //     StaticImages[i].sliceIndex,
    //     StaticImages[i].imageUID,
    //     StaticImages[i].data,
    //     StaticImages[i].shape,
    //     StaticImages[i].type
    //   );
    //   this.StaticImageArray.push(staticImage);
    // }

    for (let i = 0; i < SalientIndexes.length; i++) {
      const { type, data, sliceIndex } = SalientIndexes[i];
      const salientObject = new SalientIndex(type, data, sliceIndex);
      this.SalientPhases.push(salientObject);
    }

    for (let i = 0; i < BiomarkerAHADataArray.length; i++) {
      const { biomarkerName, data, sliceIndex, range }
        = BiomarkerAHADataArray[i];
      // TODO: remove legacy biomarkers (number[][])
      if (Array.isArray(data[0])) {
        continue;
      }
      const biomarkerAHAData = new BiomarkerAHAData(
        biomarkerName,
        data as number[],
        sliceIndex,
        range,
      );
      this.BiomarkerAHADataArray.push(biomarkerAHAData);
    }

    for (let i = 0; i < LabelProbabilities.length; i++) {
      const { sliceIndex, data } = LabelProbabilities[i];
      this.LabelProbabilitiesArray.push({
        sliceIndex,
        data: { ...data },
      });
    }
  }

  public addBiomarkerMasks(
    masks: (BiomarkerMask | BiomarkerPixelMask)[],
  ): void {
    for (const maskItem of masks) {
      const mask
        = maskItem instanceof BiomarkerMask
          ? maskItem
          : new BiomarkerMask(
              maskItem.isDynamic,
              maskItem.biomarkerName,
              maskItem.imageUID,
              maskItem.data,
              maskItem.sliceIndex,
              maskItem.refImageType,
              maskItem.range,
            );

      const existingIdx = this.BiomarkerMasksArray.findIndex(
        m =>
          m.getImageUID() === mask.getImageUID()
          && m.getBiomarkerName() === mask.getBiomarkerName(),
      );

      if (existingIdx >= 0) {
        this.BiomarkerMasksArray[existingIdx] = mask;
      }
      else {
        this.BiomarkerMasksArray.push(mask);
      }
    }
    this.availableBiomarkerMasks = {};
  }

  public addAnalyzedSeries(
    seriesInput: AnalysisSelectedSeries | AnalysisSelectedSeries[],
  ): void {
    const seriesArray = Array.isArray(seriesInput)
      ? seriesInput
      : [seriesInput];
    for (const s of seriesArray) {
      const exists = this.analyzedSeries.some(
        existing =>
          existing.seriesIdx === s.seriesIdx
          && existing.sliceIndex === s.sliceIndex,
      );
      if (!exists) {
        this.analyzedSeries.push(s);
      }
    }
  }

  public addBiomarkerCurves(
    curvesInput: (BiomarkerOCGCurve | BiomarkerOCGCurveType)[],
  ): void {
    for (const curveItem of curvesInput) {
      const curve
        = curveItem instanceof BiomarkerOCGCurve
          ? curveItem
          : new BiomarkerOCGCurve(
              curveItem.biomarkerName,
              curveItem.territory,
              curveItem.normalization,
              curveItem.data,
              curveItem.sliceIndex,
              curveItem.interpolatedIndexes,
              curveItem.isCycleNormalized,
              curveItem.cycleType,
              curveItem.startFrame,
              curveItem.endFrame,
              curveItem.esFrame,
              curveItem.esPhaseIndex,
              curveItem.edPhaseIndexes,
            );

      const existingIdx = this.BiomarkerCurvesArray.findIndex(
        c =>
          c.getSliceIndex() === curve.getSliceIndex()
          && c.getBiomarkerName() === curve.getBiomarkerName()
          && c.getTerritory() === curve.getTerritory(),
      );

      if (existingIdx >= 0) {
        this.BiomarkerCurvesArray[existingIdx] = curve;
      }
      else {
        this.BiomarkerCurvesArray.push(curve);
      }
    }
    this.availableBiomarkerCurvesByTerritory = {};
  }

  public addSalientIndexes(phases: SalientIndexItem[]): void {
    for (const item of phases) {
      const salientObj = new SalientIndex(
        item.type,
        item.data,
        item.sliceIndex,
      );
      const existingIdx = this.SalientPhases.findIndex(
        p =>
          p.getSliceIndex() === item.sliceIndex
          && p.getType() === item.type,
      );
      if (existingIdx >= 0) {
        this.SalientPhases[existingIdx] = salientObj;
      }
      else {
        this.SalientPhases.push(salientObj);
      }
    }
  }

  public addAnalysisResult(
    analysisResult: Partial<AnalysisResult>,
  ): void {
    if (analysisResult.Metadata?.selectedSeries) {
      this.addAnalyzedSeries(analysisResult.Metadata.selectedSeries);
    }
    if (analysisResult.Metadata?.interpolatedImageIndexes) {
      this.interpolatedImageIndexes.push(
        ...analysisResult.Metadata.interpolatedImageIndexes,
      );
    }
    if (analysisResult.BiomarkerMasks) {
      this.addBiomarkerMasks(analysisResult.BiomarkerMasks);
    }
    if (analysisResult.BiomarkerCurves) {
      this.addBiomarkerCurves(analysisResult.BiomarkerCurves);
    }
    if (analysisResult.SalientIndexes) {
      this.addSalientIndexes(analysisResult.SalientIndexes);
    }
  }

  public isImageUIDinAnalysis(imageUID: string): boolean {
    return this.BiomarkerMasksArray.some(
      mask => mask.getImageUID() === imageUID,
    );
  }

  public serialize(): AnalysisResult {
    return {
      BiomarkerMasks: this.BiomarkerMasksArray.map<BiomarkerPixelMask>(
        mask => ({
          biomarkerName: mask.getBiomarkerName() as BiomarkerType,
          imageUID: mask.getImageUID(),
          data: BiomarkerManager.cloneMaskData(mask.getData()),
          isDynamic: mask.getIsDynamic(),
          sliceIndex: mask.getSliceIndex(),
          refImageType: mask.getRefImageType(),
          range: mask.getRange(),
        }),
      ),
      BiomarkerCurves: this.BiomarkerCurvesArray.map(curve => ({
        biomarkerName: curve.getBiomarkerName(),
        territory: curve.getTerritory(),
        normalization: curve.getNormalization(),
        data: [...curve.getData()],
        sliceIndex: curve.getSliceIndex(),
        interpolatedIndexes: curve.getInterpolatedIndexes(),
        isCycleNormalized: curve.isCycleNormalized(),
        cycleType: curve.getCycleType(),
        startFrame: curve.getStartFrame(),
        endFrame: curve.getEndFrame(),
        esFrame: curve.getEsFrame(),
        esPhaseIndex: curve.getEsPhaseIndex(),
        edPhaseIndexes: curve.getEdPhaseIndexes(),
      })),
      BiomarkerQuantitativeData: this.BiomarkerQuantitativeDataArray.map(
        item => ({
          biomarkerName: item.getBiomarkerName() as BiomarkerType,
          data: BiomarkerManager.cloneNumericData(item.getData()),
          sliceIndex: item.getSliceIndex(),
        }),
      ),
      BiomarkerAHAData: this.BiomarkerAHADataArray.map(item => ({
        biomarkerName: item.getBiomarkerName() as BiomarkerType,
        data: [...item.getData()],
        sliceIndex: item.getSliceIndex(),
        range: item.getRange(),
      })),
      SalientIndexes: this.SalientPhases.map<SalientIndexItem>(phase => ({
        type: phase.getType() as SalientIndexType,
        data: [...phase.getData()],
        sliceIndex: phase.getSliceIndex(),
      })),
      LabelProbabilities: this.LabelProbabilitiesArray.map(entry => ({
        sliceIndex: entry.sliceIndex,
        data: { ...entry.data },
      })),
      Metadata: {
        interpolatedImageIndexes: this.interpolatedImageIndexes.map(item => ({
          ...item,
        })),
        selectedSeries: this.analyzedSeries.map(series => ({
          ...series,
        })),
      },
    };
  }

  public deserialize(serializedState: AnalysisResult): void {
    this.populateFromResponse(serializedState);
  }

  public getBiomarkerNormalRange(
    biomarkerType: BiomarkerType | string,
  ): [number, number] | null {
    const biomarkerInfo = this.biomarkerInfo[biomarkerType as BiomarkerType];
    const min = biomarkerInfo?.min;
    const max = biomarkerInfo?.max;

    if (typeof min !== 'number' || typeof max !== 'number') return null;

    return [min, max];
  }

  public getBiomarkerNormalRangeLabel(
    biomarkerType: BiomarkerType | string,
  ): string | null {
    const biomarkerInfo = this.biomarkerInfo[biomarkerType as BiomarkerType];
    return biomarkerInfo?.normal?.label ?? null;
  }

  public isSeriesAnalyzed(seriesIdx: number, sliceIndex: number): boolean {
    return this.analyzedSeries.some(
      series =>
        series.seriesIdx === seriesIdx && series.sliceIndex === sliceIndex,
    );
  }

  public getStaticMaskBySliceAndType(
    seriesUID: string,
    sliceIndex: number,
    biomarkerType: BiomarkerType,
  ): BiomarkerMask | null {
    if (sliceIndex === null) return null;
    if (biomarkerType === null) return null;
    const combinedKey = `${seriesUID}_${sliceIndex}`;

    const maskArray = this.BiomarkerMasksArray.filter(
      mask =>
        mask.getSliceIndex() === combinedKey
        && mask.getBiomarkerName() === biomarkerType
        && !mask.getIsDynamic(),
    );
    return maskArray[0] || null;
  }

  public getMaskByImageUIDAndType(
    imageUID: string,
    biomarkerType: BiomarkerType,
  ): BiomarkerMask | null {
    if (imageUID === null) return null;
    if (biomarkerType === null) return null;

    const maskArray = this.BiomarkerMasksArray.filter(
      mask =>
        mask.getImageUID() === imageUID
        && mask.getBiomarkerName() === biomarkerType,
    );
    return maskArray[0];
  }

  public getBiomarkerCurveBySliceIndexTypeAndTerritory(
    seriesUID: string,
    sliceIndex: number,
    biomarkerName: OCGCurveType,
    territory: OCGTerritory,
  ): BiomarkerOCGCurve | null {
    const combinedKey = `${seriesUID}_${sliceIndex}`;

    const curveArray = this.BiomarkerCurvesArray.filter(
      curve =>
        (curve.getSliceIndex() === combinedKey
          || curve.getSliceIndex() === `${sliceIndex}`)
        && curve.getBiomarkerName() === biomarkerName
        && curve.getTerritory() === territory,
    );
    return curveArray[0] || null;
  }

  public getLabelFromCurveType(curveType: OCGCurveType): string {
    return this.curveTypeToLabelMap[curveType] || curveType;
  }

  public getLabelFromBiomarkerType(
    biomarkerType: BiomarkerType | string,
  ): string {
    return (
      this.biomarkerInfo[biomarkerType as BiomarkerType]?.label
      || biomarkerType
    );
  }

  public getBiomarkerCurveDefaultSelection(): Partial<
    Record<OCGCurveType, OCGTerritory[]>
  > {
    const priorityCurveType: OCGCurveType = 'MYOSI';
    const priorityTerritories: OCGTerritory[] = ['LAD', 'RCA', 'LCx'];

    const availableCurveTypes = this.getAvailableCurveTypes();

    if (availableCurveTypes.includes(priorityCurveType)) {
      const priorityAvailableTerritories
        = this.getCurveTerritoriesByBiomarkerType(priorityCurveType);

      if (priorityAvailableTerritories.length) {
        const selectedPriorityTerritories = priorityTerritories.filter(
          territory => priorityAvailableTerritories.includes(territory),
        );

        if (selectedPriorityTerritories.length) {
          return {
            [priorityCurveType]: selectedPriorityTerritories,
          };
        }

        return {
          [priorityCurveType]: [priorityAvailableTerritories[0]],
        };
      }
    }

    const firstAvailableCurveType = availableCurveTypes[0];
    if (firstAvailableCurveType) {
      const territories = this.getCurveTerritoriesByBiomarkerType(
        firstAvailableCurveType,
      );
      if (territories.length) {
        return {
          [firstAvailableCurveType]: [territories[0]],
        };
      }
    }

    return {};
  }

  public getQuantitativeDataBySlice(
    seriesUID: string,
    sliceIndex: number,
  ): BiomarkerQuantitativeData[] {
    const combinedKey = `${seriesUID}_${sliceIndex}`;
    return this.BiomarkerQuantitativeDataArray.filter(
      data =>
        data.getSliceIndex() === combinedKey
        || data.getSliceIndex() === `${sliceIndex}`,
    );
  }

  public static getBiomarkerGroup(biomarkerName: string): BiomarkerGroupType {
    const lower = biomarkerName.trim().toLowerCase();

    // Regional candidates (raw key or clinical label)
    const regionalKeys = [
      'ocgdiamaxintensity',
      'ocgentropy',
      'bmore',
      'bmoremax',
      'temporalocgmedian',
      'vasoconstriction',
      'siaha16',
      'siaha16sm',
      'simocoaha16',
      'b-more diastole',
      'b-more micro',
      'b-more',
      'b-more max',
      'temporal ocg median',
      'regional_oxygenation_wall_vulnerability',
      'regional_ed_oxygenation_wall_vulnerability',
      'circumferential_strain_spectral_bandwidth',
      'image_es_nonresponse_mask',
      'image_ed_nonresponse_mask',
      'stress_to_recovery_ocg_absolute_remodeling_area',
      'regional es oxygenation-wall vulnerability',
      'regional ed oxygenation-wall vulnerability',
      'circumferential strain spectral bandwidth',
      'image es oxygenation nonresponse',
      'image ed oxygenation nonresponse',
      'stress-to-recovery absolute ocg remodeling area',
    ];

    if (regionalKeys.includes(lower)) {
      return 'regional';
    }

    if (lower.startsWith('stress')) {
      return 'stress';
    }

    if (lower.startsWith('recovery')) {
      return 'recovery';
    }

    return 'other';
  }

  public getQuantitativeDataBySliceAndGroup(
    seriesUID: string,
    sliceIndex: number,
    group: 'stress' | 'recovery' | 'other',
  ): BiomarkerQuantitativeData[] {
    const sliceData = this.getQuantitativeDataBySlice(seriesUID, sliceIndex);
    return sliceData.filter(
      item => BiomarkerManager.getBiomarkerGroup(item.getBiomarkerName()) === group,
    );
  }

  public getAvailableQuantitativeBiomarkerTypes(
    seriesUID?: string,
    sliceIndex?: number,
  ): string[] {
    let items = this.BiomarkerQuantitativeDataArray;
    if (seriesUID !== undefined && sliceIndex !== undefined) {
      const combinedKey = `${seriesUID}_${sliceIndex}`;
      items = items.filter(
        d =>
          d.getSliceIndex() === combinedKey
          || d.getSliceIndex() === `${sliceIndex}`,
      );
    }
    const names = items.map(d => d.getBiomarkerName());
    return Array.from(new Set(names));
  }

  public getAvailableAHABiomarkerTypes(
    seriesUID?: string,
    sliceIndex?: number,
  ): BiomarkerType[] {
    let items = this.BiomarkerAHADataArray;
    if (seriesUID !== undefined && sliceIndex !== undefined) {
      const combinedKey = `${seriesUID}_${sliceIndex}`;
      items = items.filter(
        d =>
          d.getSliceIndex() === combinedKey
          || d.getSliceIndex() === `${sliceIndex}`,
      );
    }
    const names = items.map(d => d.getBiomarkerName() as BiomarkerType);
    return Array.from(new Set(names));
  }

  public getQuantitativeDataBySliceIndexAndType(
    seriesUID: string,
    sliceIndex: number,
    biomarkerName: string,
  ): BiomarkerQuantitativeData[] {
    const combinedKey = `${seriesUID}_${sliceIndex}`;
    return this.BiomarkerQuantitativeDataArray.filter(
      data =>
        (data.getSliceIndex() === combinedKey
          || data.getSliceIndex() === `${sliceIndex}`)
        && data.getBiomarkerName() === biomarkerName,
    );
  }

  public getAHADataBySliceIndexAndType(
    seriesUID: string,
    sliceIndex: number,
    biomarkerName: string,
  ): BiomarkerAHAData[] {
    const combinedKey = `${seriesUID}_${sliceIndex}`;
    return this.BiomarkerAHADataArray.filter(
      data =>
        (data.getSliceIndex() === combinedKey
          || data.getSliceIndex() === `${sliceIndex}`)
        && data.getBiomarkerName() === biomarkerName,
    );
  }

  public getLabelProbabilitiesBySliceIndex(
    seriesUID: string,
    sliceIndex: number,
  ): LabelProbabilityData | null {
    const combinedKey = `${seriesUID}_${sliceIndex}`;
    const labelProbabilities = this.LabelProbabilitiesArray.find(
      entry => entry.sliceIndex === combinedKey,
    );
    return labelProbabilities || null;
  }

  public getLabelProbabilityBySliceIndexAndLabel(
    seriesUID: string,
    sliceIndex: number,
    label: string,
  ): number | null {
    const labelProbabilities = this.getLabelProbabilitiesBySliceIndex(
      seriesUID,
      sliceIndex,
    );
    if (!labelProbabilities) {
      return null;
    }

    const probability = labelProbabilities.data[label];
    return Number.isFinite(probability) ? probability : null;
  }

  public getAHATerritoryMean(
    biomarkerName: string,
    territory: OCGTerritory,
    seriesSlice?: { seriesUID: string; sliceIndex: number },
  ): number | null {
    let datas: BiomarkerAHAData[];
    if (seriesSlice) {
      const { seriesUID, sliceIndex } = seriesSlice;
      datas = this.getAHADataBySliceIndexAndType(
        seriesUID,
        sliceIndex,
        biomarkerName,
      );
    }
    else {
      datas = this.BiomarkerAHADataArray.filter(
        data => data.getBiomarkerName() === biomarkerName,
      );
    }
    if (datas.length === 0) {
      return null;
    }
    const values = datas.map(data => data.getTerritoryMean(territory).result);
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  }

  public getAHATerritoryMedian(
    biomarkerName: string,
    territory: OCGTerritory,
    seriesSlice?: { seriesUID: string; sliceIndex: number },
  ): number | null {
    let datas: BiomarkerAHAData[];
    if (seriesSlice) {
      const { seriesUID, sliceIndex } = seriesSlice;
      datas = this.getAHADataBySliceIndexAndType(
        seriesUID,
        sliceIndex,
        biomarkerName,
      );
    }
    else {
      datas = this.BiomarkerAHADataArray.filter(
        data => data.getBiomarkerName() === biomarkerName,
      );
    }
    if (datas.length === 0) {
      return null;
    }
    const values = datas.map(
      data => data.getTerritoryMedian(territory).result,
    );
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  }

  public getAHATerritoryMeanOfSlices(
    biomarkerName: string,
    territory: OCGTerritory,
  ): number | null {
    const datas = this.BiomarkerAHADataArray.filter(
      data => data.getBiomarkerName() === biomarkerName,
    );
    if (datas.length === 0) {
      return null;
    }
    const values = datas
      .map(data => data.getTerritoryMedian(territory)?.result)
      .filter(
        (val): val is number => typeof val === 'number' && Number.isFinite(val),
      );

    if (values.length === 0) {
      return null;
    }
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  }

  public getAHASegmentValue(
    biomarkerName: string,
    segment: number,
    seriesSlice?: { seriesUID: string; sliceIndex: number },
  ): number | null {
    let datas: BiomarkerAHAData[];
    if (seriesSlice) {
      const { seriesUID, sliceIndex } = seriesSlice;
      datas = this.getAHADataBySliceIndexAndType(
        seriesUID,
        sliceIndex,
        biomarkerName,
      );
    }
    else {
      datas = this.BiomarkerAHADataArray.filter(
        data => data.getBiomarkerName() === biomarkerName,
      );
    }
    if (datas.length === 0) {
      return null;
    }
    const values = datas.map(data => data.getSegmentValue(segment));
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  }

  public getSalientIndexBySliceIndex(
    seriesUID: string,
    sliceIndex: number,
  ): SalientIndex[] {
    const combinedKey = `${seriesUID}_${sliceIndex}`;
    return this.SalientPhases.filter(
      salient => salient.getSliceIndex() === combinedKey,
    );
  }

  public getSalientIndexBySliceIndexAndType(
    seriesUID: string,
    sliceIndex: number,
    type: string,
  ): SalientIndex[] {
    const combinedKey = `${seriesUID}_${sliceIndex}`;
    return this.SalientPhases.filter(
      salient =>
        salient.getSliceIndex() === combinedKey && salient.getType() === type,
    );
  }

  public getMaskTypesBasedOnBiomarkerType(
    type: MaskTypeBehaviour,
  ): BiomarkerType[] {
    const cachedTypes = this.availableBiomarkerMasks[type];
    if (cachedTypes) {
      return cachedTypes;
    }

    const biomarkerTypes = this.getMasksByBehaviour(type)
      .map(mask => mask.getBiomarkerName())
      .filter((name, index, names) => names.indexOf(name) === index);

    this.availableBiomarkerMasks[type] = biomarkerTypes as BiomarkerType[];
    return this.availableBiomarkerMasks[type];
  }

  private getMasksByBehaviour(type: MaskTypeBehaviour): BiomarkerMask[] {
    switch (type) {
      case 'Static':
        return this.BiomarkerMasksArray.filter(
          mask =>
            !mask.getIsDynamic() && mask.getRefImageType() !== 'PolarMap',
        );
      case 'Dynamic':
        return this.BiomarkerMasksArray.filter(mask => mask.getIsDynamic());
      case 'PolarMap':
        return this.BiomarkerMasksArray.filter(
          mask => mask.getRefImageType() === 'PolarMap',
        );
      default:
        return [];
    }
  }

  public getAvailableCurveTypes(): OCGCurveType[] {
    const curveTypes = this.BiomarkerCurvesArray.map(curve =>
      curve.getBiomarkerName(),
    );
    // Return the set of unique curve types
    return Array.from(new Set(curveTypes));
  }

  /**
   * Checks if the study has all the required data to be considered complete
   * @returns {boolean} True if the study is complete, false otherwise
   */
  public isStudyComplete(): boolean {
    // Check if we have masks
    if (this.BiomarkerMasksArray.length === 0) {
      return false;
    }

    // Check if we have curves
    if (this.BiomarkerCurvesArray.length === 0) {
      return false;
    }

    // Check if we have both ED and ES salient phases
    const hasED = this.SalientPhases.some(phase => phase.getType() === 'ED');
    const hasES = this.SalientPhases.some(phase => phase.getType() === 'ES');

    if (!hasED || !hasES) {
      return false;
    }

    return true;
  }

  public getMaxAHA(biomarkerName: string): number | null {
    const datas = this.BiomarkerAHADataArray.filter(
      data => data.getBiomarkerName() === biomarkerName,
    );
    if (datas.length === 0) {
      return null;
    }
    const maxValues = datas.map(data => data.getMax());
    return Math.max(...maxValues);
  }

  public getMinAHA(biomarkerName: string): number | null {
    const datas = this.BiomarkerAHADataArray.filter(
      data => data.getBiomarkerName() === biomarkerName,
    );
    if (datas.length === 0) {
      return null;
    }
    const minValues = datas.map(data => data.getMin());
    return Math.min(...minValues);
  }

  public getRangeAHA(biomarkerName: string): number | null {
    const max = this.getMaxAHA(biomarkerName);
    const min = this.getMinAHA(biomarkerName);
    if (max === null || min === null) {
      return null;
    }
    return max - min;
  }

  /**
   * Retrieves all available territories for a given OCG curve type.
   * Caches results on first call for efficiency.
   * @param biomarkerName - The OCG curve type to retrieve territories for
   * @returns Array of unique territories for the given biomarker curve type
   */
  public getCurveTerritoriesByBiomarkerType(biomarkerName: OCGCurveType) {
    const cachedTerritories
      = this.availableBiomarkerCurvesByTerritory[biomarkerName];
    if (cachedTerritories) {
      return cachedTerritories;
    }

    const allTerritories = this.BiomarkerCurvesArray.filter(
      curve => curve.getBiomarkerName() === biomarkerName,
    )
      .map(curve => curve.getTerritory())
      .filter(
        (territory, index, territories) =>
          territories.indexOf(territory) === index,
      );

    this.availableBiomarkerCurvesByTerritory[biomarkerName] = allTerritories;
    return allTerritories;
  }

  private resetState(): void {
    this.BiomarkerMasksArray = [];
    this.BiomarkerCurvesArray = [];
    this.BiomarkerQuantitativeDataArray = [];
    this.BiomarkerAHADataArray = [];
    this.LabelProbabilitiesArray = [];
    this.SalientPhases = [];
    this.analyzedSeries = [];
    this.interpolatedImageIndexes = [];
    this.availableBiomarkerMasks = {};
    this.availableBiomarkerCurvesByTerritory = {};
  }

  private static cloneNumericData(
    data: (number | null)[] | (number | null)[][],
  ): (number | null)[] | (number | null)[][] {
    if (Array.isArray(data[0])) {
      return (data as (number | null)[][]).map(row => [...row]);
    }

    return [...(data as (number | null)[])];
  }

  private static cloneMaskData(data: {
    values: number[];
    coords: [number, number][];
  }) {
    return {
      values: [...data.values],
      coords: data.coords.map(
        ([row, column]) => [row, column] as [number, number],
      ),
    };
  }
}
